import React, { useState, useEffect } from 'react';
import { PublicKey, LAMPORTS_PER_SOL, SystemProgram } from '@solana/web3.js';
import { BN } from '@coral-xyz/anchor';

function GameLobby({ program, wallet, connection, onJoinGame, onRefresh }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buyIn, setBuyIn] = useState('0.1');
  const [creating, setCreating] = useState(false);

  // Fetch all games
  const fetchGames = async () => {
    if (!program) return;
    setLoading(true);
    try {
      const allGames = await program.account.game.all();
      setGames(allGames.map(g => ({
        publicKey: g.publicKey,
        ...g.account,
      })));
    } catch (e) {
      console.error('Error fetching games:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGames();
    const interval = setInterval(fetchGames, 10000);
    return () => clearInterval(interval);
  }, [program]);

  // Create new game
  const createGame = async () => {
    if (!program || !wallet.publicKey) return;
    setCreating(true);
    
    try {
      const gameId = new BN(Date.now());
      const buyInLamports = new BN(parseFloat(buyIn) * LAMPORTS_PER_SOL);
      
      const [gamePda] = PublicKey.findProgramAddressSync(
        [Buffer.from('game'), gameId.toArrayLike(Buffer, 'le', 8)],
        program.programId
      );

      const tx = await program.methods
        .createGame(gameId, buyInLamports)
        .accounts({
          game: gamePda,
          creator: wallet.publicKey,
          systemProgram: SystemProgram.programId,
        })
        .rpc();

      console.log('Game created:', tx);
      await fetchGames();
      onRefresh();
    } catch (e) {
      console.error('Error creating game:', e);
      alert('Failed to create game: ' + e.message);
    }
    setCreating(false);
  };

  // Join game
  const joinGame = async (game) => {
    if (!program || !wallet.publicKey) return;
    setLoading(true);
    
    try {
      const tx = await program.methods
        .joinGame()
        .accounts({
          game: game.publicKey,
          player: wallet.publicKey,
          systemProgram: SystemProgram.programId,
        })
        .rpc();

      console.log('Joined game:', tx);
      onJoinGame(game);
      onRefresh();
    } catch (e) {
      console.error('Error joining game:', e);
      alert('Failed to join game: ' + e.message);
    }
    setLoading(false);
  };

  // Get status display
  const getStatus = (status) => {
    if (status.waitingForPlayer) return '⏳ Waiting';
    if (status.dealing) return '🃏 Dealing';
    if (status.betting) return '💰 Betting';
    if (status.showdown) return '🎯 Showdown';
    if (status.finished) return '✅ Finished';
    return 'Unknown';
  };

  const canJoin = (game) => {
    return game.status.waitingForPlayer && 
           game.player1?.toString() !== wallet.publicKey?.toString();
  };

  const isMyGame = (game) => {
    return game.player1?.toString() === wallet.publicKey?.toString() ||
           game.player2?.toString() === wallet.publicKey?.toString();
  };

  return (
    <div style={styles.container}>
      {/* Create Game Section */}
      <div style={styles.createSection}>
        <h2 style={styles.sectionTitle}>Create New Game</h2>
        <div style={styles.createForm}>
          <div style={styles.inputGroup}>
            <label>Buy-in (SOL)</label>
            <input
              type="number"
              value={buyIn}
              onChange={(e) => setBuyIn(e.target.value)}
              style={styles.input}
              step="0.01"
              min="0.01"
            />
          </div>
          <div style={styles.feeInfo}>
            <span style={styles.feeIcon}>🔥</span>
            <span style={styles.feeText}>
              2% platform fee → used to buy & burn <strong style={{color:'#ffcc02'}}>$PP</strong>
            </span>
          </div>
          <div style={styles.feeBreakdown}>
            <div style={styles.feeRow}>
              <span>Buy-in</span>
              <span>{parseFloat(buyIn || 0).toFixed(4)} SOL</span>
            </div>
            <div style={styles.feeRow}>
              <span>Platform fee (2%)</span>
              <span style={{color:'#ff6b35'}}>-{(parseFloat(buyIn || 0) * 0.02).toFixed(4)} SOL</span>
            </div>
            <div style={{...styles.feeRow, ...styles.feeTotal}}>
              <span>Net to pot</span>
              <span style={{color:'#4caf50'}}>{(parseFloat(buyIn || 0) * 0.98).toFixed(4)} SOL</span>
            </div>
          </div>
          <button 
            onClick={createGame} 
            disabled={creating}
            style={styles.createBtn}
          >
            {creating ? 'Creating...' : 'Create Game'}
          </button>
        </div>
      </div>

      {/* Games List */}
      <div style={styles.gamesSection}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Available Games</h2>
          <button onClick={fetchGames} style={styles.refreshBtn}>
            🔄 Refresh
          </button>
        </div>

        {loading ? (
          <p style={styles.loading}>Loading games...</p>
        ) : games.length === 0 ? (
          <p style={styles.empty}>No games available. Create one!</p>
        ) : (
          <div style={styles.gamesList}>
            {games.map((game) => (
              <div 
                key={game.publicKey.toString()} 
                style={{
                  ...styles.gameCard,
                  ...(isMyGame(game) ? styles.myGame : {}),
                }}
              >
                <div style={styles.gameInfo}>
                  <div style={styles.gameId}>
                    Game #{game.gameId.toString().slice(-6)}
                  </div>
                  <div style={styles.gameMeta}>
                    <span>Buy-in: {(game.buyIn.toNumber() / LAMPORTS_PER_SOL).toFixed(4)} SOL</span>
                    <span>Pot: {(game.pot.toNumber() / LAMPORTS_PER_SOL).toFixed(4)} SOL</span>
                    <span>Status: {getStatus(game.status)}</span>
                  </div>
                  <div style={styles.players}>
                    <span>P1: {game.player1 ? game.player1.toString().slice(0,8) + '...' : 'None'}</span>
                    <span>P2: {game.player2 ? game.player2.toString().slice(0,8) + '...' : 'Waiting'}</span>
                  </div>
                </div>
                <div style={styles.gameActions}>
                  {canJoin(game) && (
                    <button 
                      onClick={() => joinGame(game)}
                      style={styles.joinBtn}
                    >
                      Join Game
                    </button>
                  )}
                  {isMyGame(game) && !game.status.waitingForPlayer && (
                    <button 
                      onClick={() => onJoinGame(game)}
                      style={styles.playBtn}
                    >
                      Continue
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: '2rem',
  },
  createSection: {
    background: 'rgba(255,255,255,0.05)',
    padding: '1.5rem',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  sectionTitle: {
    fontSize: '1.2rem',
    marginBottom: '1rem',
  },
  createForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  input: {
    padding: '0.75rem',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '1rem',
  },
  createBtn: {
    padding: '0.75rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '4px',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: '600',
  },
  gamesSection: {
    background: 'rgba(255,255,255,0.05)',
    padding: '1.5rem',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  refreshBtn: {
    padding: '0.5rem 1rem',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '4px',
    color: '#fff',
    cursor: 'pointer',
  },
  loading: {
    textAlign: 'center',
    color: '#888',
  },
  empty: {
    textAlign: 'center',
    color: '#888',
    padding: '2rem',
  },
  gamesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  gameCard: {
    background: 'rgba(255,255,255,0.05)',
    padding: '1rem',
    borderRadius: '6px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  myGame: {
    borderColor: '#667eea',
    background: 'rgba(102,126,234,0.1)',
  },
  gameInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  gameId: {
    fontWeight: '600',
    fontSize: '1.1rem',
  },
  gameMeta: {
    display: 'flex',
    gap: '1rem',
    color: '#aaa',
    fontSize: '0.9rem',
  },
  players: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.85rem',
    color: '#888',
  },
  gameActions: {
    display: 'flex',
    gap: '0.5rem',
  },
  joinBtn: {
    padding: '0.6rem 1.2rem',
    background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
    border: 'none',
    borderRadius: '4px',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: '600',
  },
  playBtn: {
    padding: '0.6rem 1.2rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '4px',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: '600',
  },
  feeInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.6rem 0.8rem',
    background: 'rgba(255,107,53,0.1)',
    border: '1px solid rgba(255,107,53,0.2)',
    borderRadius: '6px',
    fontSize: '0.8rem',
    color: '#ff9800',
  },
  feeIcon: {
    fontSize: '1rem',
  },
  feeText: {
    lineHeight: '1.3',
  },
  feeBreakdown: {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '6px',
    padding: '0.6rem 0.8rem',
    fontSize: '0.8rem',
  },
  feeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.2rem 0',
    color: '#aaa',
  },
  feeTotal: {
    borderTop: '1px solid rgba(255,255,255,0.1)',
    marginTop: '0.3rem',
    paddingTop: '0.4rem',
    fontWeight: '600',
    color: '#fff',
  },
};

export default GameLobby;
